import { useState, useEffect, useRef, useCallback } from 'react';
import BooksTable from '../components/books/BooksTable';
import BookForm from '../components/books/BookForm';
import BookViewModal from '../components/books/BookViewModal';
import CategoriesSection from '../components/categories/CategoriesSection';
import EtiquettesSection from '../components/etiquettes/EtiquettesSection';
import AuthorsSection from '../components/authors/AuthorsSection';
import ConfirmDeleteModal from '../components/common/ConfirmDeleteModal';
import ToastContainer from '../components/common/Toast';
import { useDebounce } from '../hooks/useDebounce';
import { useToast } from '../hooks/useToast';
import * as booksApi from '../services/booksApi';
import { getApiErrorMessage } from '../utils/apiErrors';

const Books = () => {
  // State management
  const [books, setBooks] = useState([]);
  const [pagination, setPagination] = useState({
    page: 0,
    size: 20,
    totalElements: 0,
    totalPages: 0,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingBook, setEditingBook] = useState(null);
  const [sortBy, setSortBy] = useState('date_desc');
  const [statusFilter, setStatusFilter] = useState('all');
  const [visibilityFilter, setVisibilityFilter] = useState('all');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [bookToDelete, setBookToDelete] = useState(null);
  const [viewingBook, setViewingBook] = useState(null);

  // Toast notifications
  const { toasts, removeToast, success, error: showError } = useToast();

  // Refs for request cancellation
  const abortControllerRef = useRef(null);

  // Debounce search query to reduce API calls
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  /**
   * Fetch books with current filters and pagination
   * Uses AbortController to cancel in-flight requests
   */
  const fetchBooks = useCallback(async (showFilterLoading = false) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    if (showFilterLoading) {
      setFilterLoading(true);
    } else {
      setLoading(true);
    }
    setError(null);

    const params = {
      page: pagination.page,
      size: pagination.size,
    };

    if (debouncedSearchQuery) {
      params.search = debouncedSearchQuery;
    }
    if (statusFilter && statusFilter !== 'all') {
      params.status = statusFilter;
    }
    if (visibilityFilter === 'catalog') {
      params.visibleInCatalog = true;
    } else if (visibilityFilter === 'pack_only') {
      params.visibleInCatalog = false;
    }
    if (sortBy === 'title') {
      params.sort = 'title,asc';
    } else if (sortBy === 'price') {
      params.sort = 'price,desc';
    } else if (sortBy === 'date_asc') {
      params.sort = 'createdAt,asc';
    } else if (sortBy === 'date_desc') {
      params.sort = 'createdAt,desc';
    }

    try {
      const response = await booksApi.getBooks(params, abortControllerRef.current.signal);
      const pageData = response.page ?? {};
      setBooks(response.content || response.data || []);
      setPagination({
        page: pageData.number ?? response.number ?? 0,
        size: pageData.size ?? response.size ?? 20,
        totalElements: pageData.totalElements ?? response.totalElements ?? response.total ?? 0,
        totalPages: pageData.totalPages ?? response.totalPages ?? 1,
      });
      setLoading(false);
      setFilterLoading(false);
    } catch (err) {
      if (err.message === 'REQUEST_CANCELLED') {
        return;
      }
      console.error('Error fetching books:', err);
      setError(getApiErrorMessage(err, 'Failed to load books. Please try again.'));
      setBooks([]);
      setLoading(false);
      setFilterLoading(false);
    }
  }, [pagination.page, pagination.size, debouncedSearchQuery, sortBy, statusFilter, visibilityFilter]);

  /**
   * Initial load and refetch when dependencies change
   */
  useEffect(() => {
    fetchBooks(pagination.page > 0); // Show filter loading for page changes
  }, [fetchBooks]);

  /**
   * Clean up abort controller on unmount
   */
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  /**
   * Handle search query changes
   * Reset to page 0 when searching
   */
  const handleSearchChange = (query) => {
    setSearchQuery(query);
    if (pagination.page !== 0) {
      setPagination(prev => ({ ...prev, page: 0 }));
    }
  };

  /**
   * Handle sort changes
   * Reset to page 0 when sort changes to show first page of sorted results
   */
  const handleSortChange = (newSortBy) => {
    setSortBy(newSortBy);
    if (pagination.page !== 0) {
      setPagination(prev => ({ ...prev, page: 0 }));
    }
  };

  /**
   * Handle status filter changes
   * Reset to page 0 when status filter changes
   */
  const handleStatusFilterChange = (newStatus) => {
    setStatusFilter(newStatus);
    if (pagination.page !== 0) {
      setPagination(prev => ({ ...prev, page: 0 }));
    }
  };

  /**
   * Handle page changes
   */
  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  /**
   * Handle page size changes
   */
  const handlePageSizeChange = (newSize) => {
    setPagination(prev => ({ ...prev, size: newSize, page: 0 }));
  };

  /**
   * Open the read-only view modal for a book
   */
  const handleViewBook = (book) => {
    setViewingBook(book);
  };

  /**
   * Open form for adding new book
   */
  const handleAddBook = () => {
    setEditingBook(null);
    setIsFormOpen(true);
  };

  /**
   * Open form for editing book
   * Fetches latest book data from API to ensure accuracy
   */
  const handleEditBook = async (book) => {
    try {
      // Fetch latest book data from API (includes imageUrl from processBookData)
      const latestBook = await booksApi.getBookById(book.id);

      // Pass entire book object to form (like categories and authors do)
      setEditingBook(latestBook);
      setIsFormOpen(true);
    } catch (err) {
      console.error('Error fetching book for edit:', err);
      showError(getApiErrorMessage(err), 'Erreur lors du chargement');
      setError('Failed to load book details. Please try again.');
    }
  };

  /**
   * Open delete confirmation modal
   */
  const handleDeleteBook = (book) => {
    setBookToDelete(book);
    setDeleteConfirmOpen(true);
  };

  /**
   * Confirm and execute book deletion
   * Uses optimistic update pattern - updates UI immediately
   * Performs SOFT DELETE (sets active=false)
   */
  const confirmDeleteBook = async () => {
    if (!bookToDelete) return;

    const bookId = bookToDelete.id;

    try {
      // Optimistic update - remove from UI immediately
      setBooks(books.filter(b => b.id !== bookId));
      setDeleteConfirmOpen(false);
      setBookToDelete(null);

      // Execute soft deletion (sets active=false)
      await booksApi.deleteBook(bookId);

      // Update pagination count
      setPagination(prev => ({
        ...prev,
        totalElements: prev.totalElements - 1,
      }));

      // If current page is now empty and not the first page, go back one page
      if (books.length === 1 && pagination.page > 0) {
        setPagination(prev => ({ ...prev, page: prev.page - 1 }));
      } else if (books.length <= 1) {
        // If last item on first page, refetch to get accurate count
        fetchBooks();
      }

      success('Le livre a été supprimé avec succès');
    } catch (err) {
      console.error('Error deleting book:', err);
      const errorMessage = getApiErrorMessage(err);
      showError(errorMessage, 'Erreur lors de la suppression');
      setError(errorMessage);
      // Revert optimistic update by refetching
      fetchBooks();
    }
  };

  /**
   * Cancel deletion
   */
  const cancelDeleteBook = () => {
    setDeleteConfirmOpen(false);
    setBookToDelete(null);
  };

  /**
   * Handle book form submission (create or update)
   * Uses differential update - only updates the changed item
   */
  const handleSubmitForm = async (bookData, coverImage = null, categoryId = null, etiquetteId = null) => {
    try {
      let savedBook;

      if (editingBook) {
        // Update existing book
        savedBook = await booksApi.updateBook(editingBook.id, bookData, coverImage);

        // Remove old CATEGORY and ETIQUETTE tags before adding new ones,
        // otherwise addTagsToBook accumulates duplicates and find() returns the stale tag.
        const oldCategoryTag = editingBook.tags?.find(t => t.type === 'CATEGORY');
        const oldEtiquetteTag = editingBook.tags?.find(t => t.type === 'ETIQUETTE');
        const tagIdsToRemove = [];
        if (oldCategoryTag) tagIdsToRemove.push(oldCategoryTag.id);
        if (oldEtiquetteTag) tagIdsToRemove.push(oldEtiquetteTag.id);
        if (tagIdsToRemove.length > 0) {
          await booksApi.removeTagsFromBook(savedBook.id, tagIdsToRemove);
        }

        // Collect tag IDs to add
        const tagIdsToAdd = [];
        if (categoryId) tagIdsToAdd.push(categoryId);
        if (etiquetteId) tagIdsToAdd.push(etiquetteId);

        // Add new tags and refetch to get updated state
        if (tagIdsToAdd.length > 0) {
          await booksApi.addTagsToBook(savedBook.id, tagIdsToAdd);
        }
        if (tagIdsToRemove.length > 0 || tagIdsToAdd.length > 0) {
          savedBook = await booksApi.getBookById(savedBook.id);
        }

        // Differential update - replace only the updated book
        setBooks(books.map(b => b.id === savedBook.id ? savedBook : b));
        success('Le livre a été mis à jour avec succès');
      } else {
        // Create new book
        savedBook = await booksApi.createBook(bookData, coverImage);

        // Collect tag IDs to add
        const tagIdsToAdd = [];
        if (categoryId) tagIdsToAdd.push(categoryId);
        if (etiquetteId) tagIdsToAdd.push(etiquetteId);

        // Add tags if any are selected
        if (tagIdsToAdd.length > 0) {
          await booksApi.addTagsToBook(savedBook.id, tagIdsToAdd);
          // Refetch to get updated tags
          savedBook = await booksApi.getBookById(savedBook.id);
        }

        // Add new book to the list (prepend to show at top)
        setBooks([savedBook, ...books]);

        // Update total count
        setPagination(prev => ({
          ...prev,
          totalElements: prev.totalElements + 1,
        }));
        success('Le livre a été créé avec succès');
      }

      setIsFormOpen(false);
      setEditingBook(null);
    } catch (err) {
      console.error('Error saving book:', err);
      showError(getApiErrorMessage(err), editingBook ? 'Erreur lors de la mise à jour' : 'Erreur lors de la création');
      throw err; // Let form handle error display
    }
  };


  // Error state with retry
  if (error && books.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-96 space-y-4">
        <div className="text-red-600 text-center">
          <svg className="w-12 h-12 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-lg font-semibold">Error Loading Books</p>
          <p className="text-sm text-gray-600 mt-2">{error}</p>
        </div>
        <button
          onClick={() => fetchBooks()}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Livres</h1>
        <p className="text-gray-600 mt-1">Gérez votre inventaire de livres</p>
      </div>

      {/* Error banner (non-blocking) */}
      {error && books.length > 0 && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative" role="alert">
          <span className="block sm:inline">{error}</span>
          <button
            onClick={() => setError(null)}
            className="absolute top-0 bottom-0 right-0 px-4 py-3"
          >
            <svg className="fill-current h-6 w-6 text-red-500" role="button" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
              <path d="M14.348 14.849a1.2 1.2 0 0 1-1.697 0L10 11.819l-2.651 3.029a1.2 1.2 0 1 1-1.697-1.697l2.758-3.15-2.759-3.152a1.2 1.2 0 1 1 1.697-1.697L10 8.183l2.651-3.031a1.2 1.2 0 1 1 1.697 1.697l-2.758 3.152 2.758 3.15a1.2 1.2 0 0 1 0 1.698z"/>
            </svg>
          </button>
        </div>
      )}

      <BooksTable
        books={books}
        onView={handleViewBook}
        onEdit={handleEditBook}
        onDelete={handleDeleteBook}
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        sortBy={sortBy}
        onSortChange={handleSortChange}
        statusFilter={statusFilter}
        onStatusFilterChange={handleStatusFilterChange}
        visibilityFilter={visibilityFilter}
        onVisibilityFilterChange={(val) => {
          setVisibilityFilter(val);
          setPagination(prev => ({ ...prev, page: 0 }));
        }}
        onAddBook={handleAddBook}
        loading={loading || filterLoading}
        pagination={pagination}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />

      {/* Categories Management Section */}
      <CategoriesSection />

      {/* Authors Management Section */}
      <AuthorsSection />

      {/* Etiquettes Management Section */}
      <EtiquettesSection />

      <BookViewModal
        isOpen={viewingBook !== null}
        book={viewingBook}
        onClose={() => setViewingBook(null)}
        onEdit={handleEditBook}
      />

      <BookForm
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setEditingBook(null);
        }}
        onSubmit={handleSubmitForm}
        initialData={editingBook}
      />

      <ConfirmDeleteModal
        isOpen={deleteConfirmOpen}
        onConfirm={confirmDeleteBook}
        onCancel={cancelDeleteBook}
        itemName={bookToDelete?.title || "ce livre"}
      />

      <ToastContainer toasts={toasts} onClose={removeToast} />
    </div>
  );
};

export default Books;
